import { useEffect, useState } from "react";
import { Link, Navigate } from "react-router-dom";
import { Loader2, Package, Droplets } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { STATUS_FLOW } from "@/data/services";

const STATUS_STYLE = {
  pending: "border-amber-400/40 text-amber-300",
  collected: "border-sky-400/40 text-sky-300",
  cleaning: "border-aqua/40 text-aqua",
  delivering: "border-violet-400/40 text-violet-300",
  completed: "border-emerald-400/40 text-emerald-300",
};

export default function Dashboard() {
  const { user, logout } = useAuth();
  const [bookings, setBookings] = useState(null);

  useEffect(() => {
    if (!user) return;
    api
      .get("/bookings")
      .then(({ data }) => setBookings(data))
      .catch(() => setBookings([]));
  }, [user]);

  if (user === null) {
    return (
      <main className="flex min-h-screen items-center justify-center bg-obsidian">
        <Loader2 className="animate-spin text-aqua" />
      </main>
    );
  }
  if (user === false) {
    return <Navigate to="/auth" state={{ from: "/dashboard" }} replace />;
  }

  const active = bookings?.filter((b) => b.status !== "completed").length ?? 0;
  const completed = bookings?.filter((b) => b.status === "completed").length ?? 0;

  return (
    <main className="min-h-screen bg-obsidian px-6 py-16 lg:px-12" data-testid="dashboard-page">
      <div className="mx-auto max-w-6xl">
        <div className="flex flex-wrap items-center justify-between gap-6">
          <Link to="/" data-testid="dashboard-logo" className="flex items-center gap-3">
            <span className="flex h-9 w-9 items-center justify-center border border-aqua/50 text-aqua">
              <Droplets size={18} />
            </span>
            <span className="font-display text-sm font-bold uppercase tracking-[0.2em] text-white">
              Clean <span className="text-aqua">&</span> Revive
            </span>
          </Link>
          <div className="flex items-center gap-6">
            <span className="font-mono text-xs uppercase tracking-[0.15em] text-zinc-500">{user.email}</span>
            <button
              data-testid="dashboard-logout-button"
              onClick={logout}
              className="font-mono text-xs uppercase tracking-[0.2em] text-zinc-400 transition-colors duration-300 hover:text-aqua"
            >
              Log out
            </button>
          </div>
        </div>

        <p className="mt-14 font-mono text-xs uppercase tracking-[0.25em] text-aqua">My bookings</p>
        <h1 className="mt-4 font-display text-4xl font-extrabold uppercase tracking-tighter text-white sm:text-5xl">
          Hey, {user.name.split(" ")[0]}.
        </h1>

        <div className="mt-10 grid gap-6 sm:grid-cols-3">
          {[
            ["Total bookings", bookings?.length ?? 0, "dashboard-stat-total"],
            ["In progress", active, "dashboard-stat-active"],
            ["Completed", completed, "dashboard-stat-completed"],
          ].map(([label, value, id]) => (
            <div key={id} data-testid={id} className="border border-white/5 bg-carbon p-6">
              <p className="font-display text-3xl font-extrabold tracking-tighter text-white">{value}</p>
              <p className="mt-2 font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-500">{label}</p>
            </div>
          ))}
        </div>

        <div className="mt-10 flex items-center justify-between">
          <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-zinc-500">History</p>
          <Link
            to="/book"
            data-testid="dashboard-new-booking-button"
            className="border border-aqua bg-aqua px-6 py-2.5 font-mono text-xs font-bold uppercase tracking-[0.2em] text-obsidian transition-colors duration-300 hover:bg-transparent hover:text-aqua"
          >
            New booking
          </Link>
        </div>

        <div className="mt-6 border border-white/5" data-testid="dashboard-bookings-list">
          {bookings === null ? (
            <div className="flex justify-center p-16">
              <Loader2 className="animate-spin text-aqua" />
            </div>
          ) : bookings.length === 0 ? (
            <div className="flex flex-col items-center gap-4 p-16 text-center">
              <Package size={28} className="text-zinc-600" />
              <p className="text-sm text-zinc-400">No bookings yet. Your trainers are waiting.</p>
              <Link to="/book" data-testid="dashboard-empty-book-button" className="font-mono text-xs uppercase tracking-[0.2em] text-aqua hover:text-white">
                Book your first clean
              </Link>
            </div>
          ) : (
            bookings.map((b, i) => (
              <div
                key={b.booking_id}
                data-testid={`booking-row-${b.ref}`}
                className={`grid gap-4 p-6 sm:grid-cols-12 sm:items-center ${i > 0 ? "border-t border-white/5" : ""}`}
              >
                <div className="sm:col-span-3">
                  <p className="font-mono text-xs font-bold tracking-[0.15em] text-aqua">{b.ref}</p>
                  <p className="mt-1 font-display text-sm font-bold uppercase tracking-tight text-white">
                    {b.service_name} x {b.pairs}
                  </p>
                </div>
                <div className="sm:col-span-3">
                  <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-500">
                    {b.fulfillment === "pickup" ? "Collection" : "Drop-off"}
                  </p>
                  <p className="mt-1 text-sm text-zinc-300">
                    {b.preferred_date} — {b.time_slot}
                  </p>
                </div>
                <div className="sm:col-span-4">
                  {b.payment_status === "paid" ? (
                    <>
                      <div className="flex items-center gap-1.5">
                        {STATUS_FLOW.map((s) => (
                          <span
                            key={s}
                            className={`h-1 flex-1 ${
                              STATUS_FLOW.indexOf(b.status) >= STATUS_FLOW.indexOf(s) ? "bg-aqua" : "bg-white/10"
                            }`}
                          />
                        ))}
                      </div>
                      <span
                        data-testid={`booking-status-${b.ref}`}
                        className={`mt-3 inline-block border px-3 py-1 font-mono text-[10px] uppercase tracking-[0.2em] ${STATUS_STYLE[b.status]}`}
                      >
                        {b.status}
                      </span>
                    </>
                  ) : (
                    <span
                      data-testid={`booking-status-${b.ref}`}
                      className="inline-block border border-amber-400/40 px-3 py-1 font-mono text-[10px] uppercase tracking-[0.2em] text-amber-300"
                    >
                      Payment incomplete
                    </span>
                  )}
                </div>
                <div className="sm:col-span-2 sm:text-right">
                  <p className="font-display text-xl font-extrabold tracking-tighter text-white">£{b.total}</p>
                </div>
              </div>
            ))
          )}
        </div>
      </div>
    </main>
  );
}
